// Constantes para Workflow Granular - Fase 1

import { cascadeFrom, addDays } from './schedule';

// Gestação bovina; só é usada aqui, então não entra na corrente de agendamento.
const GESTATION_DAYS = 280;

// ============================================================================
// PROTOCOLOS HORMONAIS (OPU)
// ============================================================================
export const HORMONAL_PROTOCOLS = {
  P36: 'P-36',
  FSH: 'FSH',
  NO_PROTOCOL: 'Sem protocolo',
  OTHER: 'Outro'
};

export const EMBRYO_QUALITY_LABELS = {
  1: 'Grau 1',
  2: 'Grau 2',
  3: 'Grau 3',
  4: 'Grau 4'
};

export const EMBRYO_STAGE_LABELS = {
  // Valores atuais
  'BN': 'BN',
  'BI': 'BI',
  'BL': 'BL',
  'BX': 'BX',
  'BX-ECLO': 'BX-ECLO',
  // Retrocompatibilidade com dados antigos
  'Bi': 'BI',
  'Bl': 'BL',
  'Bx': 'BX',
  'Be': 'BX-ECLO'
};

// ============================================================================
// QUALIDADE DO CORPO LÚTEO (TE)
// ============================================================================
export const CL_QUALITY = [
  { value: 1, label: 'G1' },
  { value: 2, label: 'G2' },
  { value: 3, label: 'G3' },
  { value: 4, label: 'G4' }
];

// ============================================================================
// STATUS DA RECEPTORA (TE)
// ============================================================================
export const RECIPIENT_STATUS = {
  APPROVED: 'APROVADA',
  REJECTED: 'REPROVADA'
};

// ============================================================================
// MÉTODOS DE DIAGNÓSTICO (PRENHEZ)
// ============================================================================
export const DIAGNOSTIC_METHODS = {
  ULTRASOUND: 'Ultrassom',
  RECTAL_PALPATION: 'Palpação Retal'
};

// ============================================================================
// RESULTADOS DG 30D
// ============================================================================
export const DG30_RESULTS = {
  PREGNANT: 'PRENHE',
  EMPTY: 'VAZIA',
  INCONCLUSIVE: 'INCONCLUSIVO'
};

// ============================================================================
// CAUSAS DE VAZIA (DG 30D)
// ============================================================================
export const EMPTY_CAUSES = {
  IMPLANTATION_FAILURE: 'Falha de implantação (nunca pegou)',
  EARLY_DEATH: 'Morte embrionária precoce (pegou mas morreu)',
  REABSORPTION: 'Reabsorção embrionária',
  UNKNOWN: 'Desconhecida'
};

// ============================================================================
// MOTIVOS DE INCONCLUSIVO (DG 30D)
// ============================================================================
export const INCONCLUSIVE_REASONS = {
  TOO_EARLY: 'Gestação muito inicial',
  POOR_CONDITIONS: 'Condições técnicas ruins',
  TECHNICIAN_DOUBT: 'Dúvida do técnico',
  OTHER: 'Outro'
};

// ============================================================================
// RESULTADOS DG 60D
// ============================================================================
export const DG60_RESULTS = {
  PREGNANT: 'PRENHE',
  ABORTED: 'ABORTOU'
};

// ============================================================================
// BATIMENTOS CARDÍACOS (DG 60D)
// ============================================================================
export const HEARTBEAT_STATUS = {
  NORMAL: 'Normal',
  WEAK: 'Fraco',
  ABSENT: 'Ausente'
};

// ============================================================================
// SEXO DO FETO
// ============================================================================
export const FETUS_SEX = {
  MALE: 'MACHO',
  FEMALE: 'FÊMEA'
};

// ============================================================================
// CONFIANÇA DA SEXAGEM
// ============================================================================
export const SEXING_CONFIDENCE = {
  HIGH: 'Alta',
  MEDIUM: 'Média',
  LOW: 'Baixa'
};

// ============================================================================
// CATEGORIAS DE ANIMAIS
// ============================================================================
export const ANIMAL_CATEGORIES = {
  DONOR: 'Doadora',
  RECIPIENT: 'Receptora',
  REPRODUCER: 'Reprodutor',
  BULL: 'Touro',
  BOTH: 'Ambos'
};

// ============================================================================
// RAÇAS COMUNS (pode expandir)
// ============================================================================
export const COMMON_BREEDS = [
  'Gir',
  'Nelore',
  'Guzerá',
  'Indubrasil',
  'Angus',
  'Hereford',
  'Brahman',
  'Girolando',
  'Holandês',
  'Jersey',
  'Senepol',
  'Tabapuã',
  'Caracu',
  'Mestiço',
  'Outra'
];

// ============================================================================
// ESTRUTURAS DE DADOS VAZIAS (Templates)
// ============================================================================

export const EMPTY_OPU_SESSION = {
  session: {
    date: null,
    time: null,
    farm: '',
    technician_id: null,
    technician_name: '',
    protocol: null
  },
  donors: [],
  semen_transport: [],
  general_observations: '',
  completed: false,
  completed_at: null
};

export const EMPTY_DONOR = {
  id: null,
  animal_id: null, // UUID do animal na tabela
  animal_identifier: '',
  rgd: '',
  breed: '',
  bull: '',
  aspiration_time: '',
  follicles: {
    left: 0,
    right: 0,
    total: 0
  },
  oocytes_recovered: 0,
  oocytes_denuded: 0,
  oocytes_viable: 0,
  recovery_rate: 0,
  fluid_quality: null,
  observations: '',
  photo_url: null
};

export const EMPTY_FIV_SESSION = {
  session: {
    reception_date: null,
    reception_time: null,
    embryologist_id: null,
    embryologist_name: '',
    opu_link: null,
    // `semen` fica DE FORA de propósito. A gravação só toca nas palhetas
    // quando a chave existe; um `[]` aqui transformaria toda sessão nova numa
    // ordem explícita de "apague o sêmen desta sessão". Ausente é o padrão
    // seguro, e a tela lê com `|| []`.
  },
  donors_processing: [],
  completed: false,
  completed_at: null
};

// Shape de um donor dentro de fiv_data (usado como template ao adicionar)
export const EMPTY_FIV_DONOR = {
  id: null,
  donor_id: null,
  donor_identifier: '',
  rgd: null,
  bull: '',
  oocytes_received: 0,
  viable_oocytes: 0,
  cleaved_oocytes: 0,
  from_opu: false,
  // Sêmen: de qual palheta da sessão vieram as gotas desta doadora.
  // `gotas` rastreia; quem baixa o estoque é a dose, lançada na sessão.
  semen_batch_id: null,
  gotas: null,
  // Contagens por estágio (D7) — o que vai para a TE. NÃO é o total
  // produzido: congelado e descarte se SOMAM a isto. Ver FIV_STAGE_FIELDS.
  stage_bi: 0,
  stage_bl: 0,
  stage_bx: 0,
  stage_bn: 0,
  stage_be: 0,
  // Destino agregado
  dest_fresh: 0,
  dest_dt: 0,
  dest_vitri: 0,
  dest_dt_obs: '',
  dest_vitri_obs: '',
  // DT por estágio (para geração correta dos embriões)
  dest_dt_bi: 0,
  dest_dt_bl: 0,
  dest_dt_bx: 0,
  dest_dt_bn: 0,
  dest_dt_be: 0,
  // Vitrificação por estágio
  dest_vitri_bi: 0,
  dest_vitri_bl: 0,
  dest_vitri_bx: 0,
  dest_vitri_bn: 0,
  dest_vitri_be: 0,
};

/**
 * Estágios relevantes para PIV (sem MO) — e o que eles contam.
 *
 * ESTES SÃO OS EMBRIÕES QUE VÃO PARA A TE, não o total produzido.
 *
 * Os quatro grupos da tela de FIV são contagens INDEPENDENTES, e o produzido
 * é a soma deles: estágios (TE) + DT + vitrificação + descarte. Ler os
 * estágios como total, com o congelamento saindo de dentro, foi o modelo
 * antigo — e ele engoliu 41 embriões em produção, porque cada vitrificado
 * ocupava a vaga de um embrião de TE.
 */
export const FIV_STAGE_FIELDS = [
  { key: 'stage_bi', stage: 'BI', label: 'BI' },
  { key: 'stage_bl', stage: 'BL', label: 'BL' },
  { key: 'stage_bx', stage: 'BX', label: 'BX' },
  { key: 'stage_bn', stage: 'BN', label: 'BN' },
  { key: 'stage_be', stage: 'BX-ECLO', label: 'BE' },
];

// Campos de DT por estágio (espelha FIV_STAGE_FIELDS, mas para destino DT)
export const FIV_DT_STAGE_FIELDS = [
  { key: 'dest_dt_bi', label: 'BI' },
  { key: 'dest_dt_bl', label: 'BL' },
  { key: 'dest_dt_bx', label: 'BX' },
  { key: 'dest_dt_bn', label: 'BN' },
  { key: 'dest_dt_be', label: 'BE' },
];

// Vitrificação por estágio. Era a única contagem solta — herança de quando
// ela apenas REPARTIA embriões que já existiam com estágio próprio. Agora ela
// os CRIA, e sem o estágio o embrião nasceria sem fase.
export const FIV_VITRI_STAGE_FIELDS = [
  { key: 'dest_vitri_bi', label: 'BI' },
  { key: 'dest_vitri_bl', label: 'BL' },
  { key: 'dest_vitri_bx', label: 'BX' },
  { key: 'dest_vitri_bn', label: 'BN' },
  { key: 'dest_vitri_be', label: 'BE' },
];

// Campos de descarte por estágio. Espelha o DT: descarte também é afirmação
// sobre embriões concretos, e não sobra de conta.
export const FIV_DESC_STAGE_FIELDS = [
  { key: 'dest_desc_bi', label: 'BI' },
  { key: 'dest_desc_bl', label: 'BL' },
  { key: 'dest_desc_bx', label: 'BX' },
  { key: 'dest_desc_bn', label: 'BN' },
  { key: 'dest_desc_be', label: 'BE' },
];

// EMPTY_EMBRYO saiu junto com a tela de FIV simplificada, a única que lançava
// embrião a embrião. A FIV conta por ESTÁGIO — quantos BI, quantos BL —, e
// `embryos` é gerado a partir dessas contagens pela gravação, não digitado.

export const EMPTY_TE_SESSION = {
  session: {
    date: null,
    farm: '',
    city: '',
    technician_id: null,
    technician_name: '',
    aspiration_date: null
  },
  transfers: [],
  completed: false,
  completed_at: null
};

export const EMPTY_PREGNANCY_DATA = {
  dg30: {
    session: {
      date: null,
      technician_id: null,
      technician_name: '',
      method: 'Ultrassom'
    },
    checks: [],
    completed: false
  },
  dg60: {
    session: {
      date: null,
      technician_id: null,
      technician_name: '',
      method: 'Ultrassom'
    },
    checks: [],
    completed: false,
    billing_quantity: 0
  }
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Calcula taxa de recuperação de óvulos
 */
export const calculateRecoveryRate = (recovered, total) => {
  if (!total || total === 0) return 0;
  return Math.round((recovered / total) * 100 * 10) / 10; // 1 casa decimal
};

/**
 * Calcula taxa de clivagem (D2)
 */
export const calculateCleavageRate = (cleaved, viable) => {
  if (!viable || viable === 0) return 0;
  return Math.round((cleaved / viable) * 100 * 10) / 10;
};

/**
 * Datas previstas pós-TE, por receptora.
 *
 * Os offsets vêm de SCHEDULE_CHAIN em vez de números soltos aqui: eram os
 * mesmos números que o cadastro usa, e manter duas cópias é como o calendário
 * e o agendamento saíam de sincronia.
 *
 * As DGs querem a data da FIV, não a da TE — a idade gestacional conta da
 * fecundação. `fivDate` é opcional porque existe TE avulsa, com embrião vindo
 * de fora e nenhuma FIV registrada; aí a âncora é a própria TE, pela distância
 * canônica entre as duas. O parto continua contado da TE.
 */
export const calculatePostTEDates = (teDate, fivDate = null) => {
  if (!teDate) return { expected_dg30: null, expected_dg60: null, expected_birth: null };

  const previstas = fivDate
    ? cascadeFrom('fiv', String(fivDate).slice(0, 10), ['fiv', 'pregnancy_check'])
    : cascadeFrom('te', String(teDate).slice(0, 10), ['te', 'pregnancy_check']);

  return {
    expected_dg30: previstas.pregnancy_30d_scheduled_date,
    expected_dg60: previstas.pregnancy_60d_scheduled_date,
    expected_birth: previsaoDeParto(teDate)
  };
};

/** A data prevista do parto de uma receptora: TE + gestação. */
export const previsaoDeParto = (teDate) => addDays(teDate, GESTATION_DAYS);

/**
 * Gera ID temporário para novos itens
 */
export const generateTempId = () => {
  return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};


// ============================================================================
// PROTOCOLO DAS RECEPTORAS
// ============================================================================

/**
 * O que se aplica na receptora, como o produtor lê no frasco.
 *
 * Sugestão para `<datalist>`, NÃO opções de `<select>`: marca muda, protocolo
 * muda, e o relatório de visita é documento — tem que dizer o que de fato
 * entrou na vaca, não o rótulo mais próximo que a tela oferecia.
 */
export const HORMONIOS_SUGERIDOS = [
  'IMPLANTE DE PROGESTERONA',
  'BENZOATO DE ESTRADIOL',
  'CIPIONATO DE ESTRADIOL',
  'eCG',
  'PGF2alfa',
  'GnRH',
  'RETIRADA DE PROTOCOLO',
];

export const PROTOCOLOS_RECEPTORA = [
  'P4 + BE — 8 dias',
  'P4 + BE — 9 dias',
  'J-Synch',
  'Outro',
];

export const MOMENTOS_DO_PROTOCOLO = [
  { id: 'inicio',   label: 'Início' },
  { id: 'retirada', label: 'Retirada' },
  { id: 'outro',    label: 'Outro' },
];

/** Dias entre o implante e a retirada, no protocolo mais usado. */
export const DIAS_ATE_A_RETIRADA = 7;

export const EMPTY_RECEPTORA = {
  id: null,
  animal_id: null,
  identifier: '',
  breed: 'Mestiço',
  ecc: null,
  ativa: true,
  observations: '',
};

export const EMPTY_APLICACAO = {
  id: null,
  data: null,
  atividade: '',
  dose: '',
  responsavel: '',
  momento: 'inicio',
};

export const EMPTY_PROTOCOLO_SESSION = {
  session: {
    date: null,
    farm: '',
    city: '',
    technician_id: null,
    technician_name: '',
    protocol_name: null,
  },
  receptoras: [],
  aplicacoes: [],
  observations: '',
  completed: false,
  completed_at: null,
};

/**
 * A data da retirada sai do CRONOGRAMA, não de um campo digitado de novo.
 *
 * Gravá-la também na sessão criaria duas verdades para o mesmo dia, e um dia
 * elas divergiriam. Quando não há linha de retirada lançada, cai em D+7, que é
 * o protocolo mais usado — e o consultor corrige se for outro.
 */
export const dataDaRetirada = (aplicacoes = [], dataDaSessao = null) => {
  const retiradas = (aplicacoes || [])
    .filter(a => a?.momento === 'retirada' && a?.data)
    .map(a => String(a.data).slice(0, 10))
    .sort();
  if (retiradas.length > 0) return retiradas[0];
  return dataDaSessao ? addDays(String(dataDaSessao).slice(0, 10), DIAS_ATE_A_RETIRADA) : null;
};

/**
 * Cronograma sugerido a partir do dia do implante.
 *
 * É ponto de partida, não regra: o consultor apaga, acrescenta e troca a dose.
 * Existe porque digitar cinco linhas do zero em toda visita é o que faz a
 * pessoa voltar para o caderno.
 */
export const cronogramaPadrao = (d0, responsavel = '') => {
  if (!d0) return [];
  const dia = String(d0).slice(0, 10);
  const retirada = addDays(dia, DIAS_ATE_A_RETIRADA);
  return [
    { ...EMPTY_APLICACAO, data: dia, atividade: 'IMPLANTE DE PROGESTERONA', dose: '1 UND', responsavel, momento: 'inicio' },
    { ...EMPTY_APLICACAO, data: dia, atividade: 'BENZOATO DE ESTRADIOL', dose: '2,0 ML', responsavel, momento: 'inicio' },
    { ...EMPTY_APLICACAO, data: retirada, atividade: 'RETIRADA DE PROTOCOLO', dose: '', responsavel, momento: 'retirada' },
    { ...EMPTY_APLICACAO, data: retirada, atividade: 'CIPIONATO DE ESTRADIOL', dose: '1,0 ML', responsavel, momento: 'retirada' },
    { ...EMPTY_APLICACAO, data: retirada, atividade: 'eCG', dose: '1,5 ML', responsavel, momento: 'retirada' },
  ];
};
